import React, { useState, useEffect, useRef } from "react"
import { Tag, Search, Plus, Check } from "lucide-react"
import { fetchTags } from "../api/tagsApi"
import { TagBadge } from "./TagBadge"

/**
 * Multi-select tag picker for attaching/detaching tags on any taggable entity.
 *
 * Props:
 *   appliedTags   – array of { id, name, color } already on this record
 *   onAdd(tag)    – called when user selects an unattached tag
 *   onRemove(tag) – called when user clicks remove on an attached tag
 *   disabled      – disables all interaction
 */
export function TagPicker({ appliedTags = [], onAdd, onRemove, disabled = false }) {
  const [open, setOpen] = useState(false)
  const [allTags, setAllTags] = useState([])
  const [q, setQ] = useState("")
  const containerRef = useRef(null)

  useEffect(() => {
    fetchTags().then((data) => setAllTags(data.tags || [])).catch(() => {})
  }, [])

  useEffect(() => {
    if (!open) return
    function handleClickOutside(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false)
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [open])

  const appliedIds = new Set(appliedTags.map((t) => t.id))
  const filtered = allTags.filter((t) =>
    !q || t.name.toLowerCase().includes(q.toLowerCase()) || t.description?.toLowerCase().includes(q.toLowerCase())
  )

  return (
    <div className="space-y-1.5">
      {/* Applied tags */}
      {appliedTags.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {appliedTags.map((tag) => (
            <TagBadge key={tag.id} tag={tag} onRemove={disabled ? undefined : onRemove} />
          ))}
        </div>
      )}

      {/* Dropdown trigger */}
      {!disabled && (
        <div className="relative" ref={containerRef}>
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="flex items-center gap-1.5 rounded-md border border-dashed border-slate-300 px-2.5 py-1 text-[12px] text-slate-500 hover:border-slate-400 hover:text-slate-700 transition-colors"
          >
            <Tag className="h-3 w-3" />
            <span>{appliedTags.length > 0 ? "Add tag" : "Add tags"}</span>
            <Plus className="h-3 w-3" />
          </button>

          {open && (
            <div className="absolute left-0 top-full z-50 mt-1 w-64 rounded-xl border border-slate-200 bg-white shadow-lg">
              <div className="border-b border-slate-100 p-2">
                <div className="flex items-center gap-2 rounded-md bg-slate-50 px-2.5 py-1.5">
                  <Search className="h-3.5 w-3.5 text-slate-400" />
                  <input
                    autoFocus
                    className="flex-1 bg-transparent text-[13px] outline-none placeholder:text-slate-400"
                    placeholder="Search tags..."
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                  />
                </div>
              </div>
              <ul className="max-h-48 overflow-y-auto py-1">
                {filtered.length === 0 ? (
                  <li className="px-3 py-2 text-[12px] text-slate-400">No tags found</li>
                ) : (
                  filtered.map((tag) => {
                    const applied = appliedIds.has(tag.id)
                    return (
                      <li key={tag.id}>
                        <button
                          type="button"
                          className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-[13px] hover:bg-slate-50 transition-colors"
                          onClick={() => {
                            if (applied) {
                              onRemove?.(tag)
                            } else {
                              onAdd?.(tag)
                            }
                          }}
                        >
                          <span
                            className="h-3 w-3 shrink-0 rounded-full"
                            style={{ backgroundColor: tag.color || "#64748B" }}
                          />
                          <span className="flex-1 text-slate-800">{tag.name}</span>
                          {applied && <Check className="h-3.5 w-3.5 text-slate-500" />}
                        </button>
                      </li>
                    )
                  })
                )}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
